import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChefHat, Users, TrendingUp, Award, Building2, Network } from "lucide-react";
import ComplimentsVideo from "../videos/compliments.mp4";

export default function Home() {
  const navigate = useNavigate();
  const featuredProjectsRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const scrollToFeaturedProjects = () => {
    featuredProjectsRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const projects = [
    {
      name: "Compliments to the Chef",
      description:
        "A revolutionary platform connecting diners with the talented chefs behind their favorite dishes. Built with React Native, React, and Firebase - enabling real-time chef-diner connections and culinary storytelling.",
      video: ComplimentsVideo,
      link: "https://www.complimentstothechef.co.uk/",
      external: true,
      tags: ["React Native", "Firebase", "Real-time", "Live Now"],
      featured: true,
    },
  ];

  const handleProjectClick = (project) => {
    if (project.external) {
      window.open(project.link, "_blank", "noopener,noreferrer");
    } else {
      navigate(project.link);
    }
  };

  return (
    <>
      <Helmet>
        <title>Morze Tech | Building Compliments to the Chef</title>
        <meta
          name="description"
          content="We're building Compliments to the Chef - a platform connecting diners with the chefs behind their favorite dishes. Currently in our growth and validation phase."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange/5">
        <div className="min-h-screen flex flex-col">
          {/* Hero Section */}
          <div className="pt-20 md:pt-32 pb-12 md:pb-20 px-4">
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center"
            >
              <motion.h1
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3 }}
                className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6 px-2"
              >
                Connecting <span className="text-orange">Diners</span> with the Chefs Behind Their Favorite Dishes
              </motion.h1>
              <p className="text-base md:text-xl text-gray-700 mb-6 md:mb-8 max-w-3xl mx-auto px-4">
                We're Morze Tech, a food-tech startup building <strong>Compliments to the Chef</strong> - where every meal becomes a story and every chef gets the recognition they deserve.
              </p>

              {/* Status Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="inline-flex items-center gap-3 bg-orange/10 border-2 border-orange/40 rounded-full px-6 py-3 mb-6 md:mb-8"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm md:text-base font-semibold text-gray-900">
                  Growth & Validation Phase | Scaling Our Community
                </span>
              </motion.div>

              <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 px-4">
                <button
                  onClick={scrollToFeaturedProjects}
                  className="bg-orange text-white px-6 md:px-8 py-3 rounded-lg hover:bg-orange-dark transition duration-300 font-medium shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  See Our Platform
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="border-2 border-orange text-orange px-6 md:px-8 py-3 rounded-lg hover:bg-orange hover:text-white transition duration-300 font-medium w-full sm:w-auto"
                >
                  Our Story
                </button>
              </div>
            </motion.div>
          </div>

          {/* The Platform */}
          <motion.section
            ref={featuredProjectsRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="w-full max-w-6xl px-4 mx-auto mb-16 md:mb-24"
          >
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                Our Platform
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto px-4">
                Currently live and growing - validating product-market fit with our initial community
              </p>
            </div>

            {/* Platform Showcase */}
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5 }}
                className="group relative rounded-2xl overflow-hidden shadow-2xl ring-2 ring-orange h-[500px] md:h-[600px]"
              >
                {/* Live Now badge */}
                <div className="absolute top-4 right-4 bg-orange text-white px-3 py-1 rounded-full text-sm font-semibold z-10 shadow-lg">
                  ✨ Live Now
                </div>

                <video
                  src={projects[0].video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent flex flex-col justify-end p-6 md:p-10">
                  <div className="mb-4 flex flex-wrap gap-2">
                    {projects[0].tags.map((tag, i) => (
                      <span
                        key={i}
                        className="bg-orange/90 text-white px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4">
                    {projects[0].name}
                  </h3>
                  <p className="text-gray-100 mb-6 text-base md:text-xl max-w-3xl">
                    {projects[0].description}
                  </p>
                  <button
                    onClick={() => handleProjectClick(projects[0])}
                    className="self-start bg-orange text-white px-8 py-3 rounded-lg hover:bg-orange-dark transition duration-300 font-medium shadow-lg text-base md:text-lg"
                  >
                    Visit Live Platform →
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.section>

          {/* Key Features - NEW SECTION */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="w-full max-w-6xl px-4 mx-auto mb-16 md:mb-24"
          >
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                What Makes Us Unique
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                Three powerful features that set us apart in the food-tech space
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 md:p-8 shadow-lg hover:shadow-xl hover:border-orange transition-all"
              >
                <Award className="w-12 h-12 md:w-14 md:h-14 mb-4 text-orange" />
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Chef Portfolios
                </h3>
                <p className="text-gray-700 text-sm md:text-base mb-4">
                  Chefs build verified portfolios backed by real diner reviews, showcasing their culinary journey and signature dishes
                </p>
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  LIVE NOW
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                whileHover={{ scale: 1.02 }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 md:p-8 shadow-lg hover:shadow-xl hover:border-orange transition-all"
              >
                <Building2 className="w-12 h-12 md:w-14 md:h-14 mb-4 text-orange" />
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Restaurant Pages
                </h3>
                <p className="text-gray-700 text-sm md:text-base mb-4">
                  Restaurants create branded pages to showcase their chefs, share reviews, and build their culinary identity
                </p>
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  LIVE NOW
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileHover={{ scale: 1.02 }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 md:p-8 shadow-lg hover:shadow-xl hover:border-orange transition-all"
              >
                <Network className="w-12 h-12 md:w-14 md:h-14 mb-4 text-orange" />
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Chef Network
                </h3>
                <p className="text-gray-700 text-sm md:text-base mb-4">
                  Chefs connect across partnered restaurants, building their reputation and network within the platform
                </p>
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  LIVE NOW
                </div>
              </motion.div>
            </div>
          </motion.section>

          {/* Why We Built This */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="w-full max-w-6xl px-4 mx-auto mb-16 md:mb-24"
          >
            <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
                Why We Built This
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <ChefHat className="w-12 h-12 mb-4 mx-auto text-orange" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Recognize Chefs</h3>
                  <p className="text-gray-700 text-sm">
                    Talented chefs work behind the scenes. We're giving them the spotlight they deserve with verified portfolios.
                  </p>
                </div>
                <div className="text-center">
                  <Users className="w-12 h-12 mb-4 mx-auto text-orange" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Connect Diners</h3>
                  <p className="text-gray-700 text-sm">
                    Every great meal has a story. We're creating meaningful connections through food and authentic reviews.
                  </p>
                </div>
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 mb-4 mx-auto text-orange" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Help Restaurants</h3>
                  <p className="text-gray-700 text-sm">
                    Authentic recognition drives loyalty. We're helping restaurants thrive through real chef-diner connections.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Where We Are */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="w-full max-w-6xl px-4 mx-auto mb-16 md:mb-24"
          >
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                Our Journey
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                We're a bootstrapped startup validating our product with real users before scaling
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold text-gray-400">COMPLETED</span>
                </div>
                <h3 className="text-xl font-bold text-gray-400 mb-2">Soft Launch</h3>
                <p className="text-gray-400 text-sm">
                  Built our initial community, gathered feedback, and proved the concept works
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange/20 to-orange/5 border-2 border-orange/50 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-bold text-green-600">CURRENT PHASE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Growth & Validation</h3>
                <p className="text-gray-700 text-sm">
                  Scaling users, refining features, and building metrics that matter
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold text-purple-600">FUTURE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scale & Investment</h3>
                <p className="text-gray-700 text-sm">
                  Using proven traction to attract strategic investors and accelerate growth
                </p>
              </div>
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="bg-gradient-to-r from-orange/10 to-transparent border-t border-gray-200 py-12 md:py-16"
          >
            <div className="max-w-4xl mx-auto text-center px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                Interested in What We're Building?
              </h2>
              <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
                Whether you're an investor, potential partner, or just curious about our journey - we'd love to connect.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
                <button
                  onClick={() => navigate("/contact")}
                  className="bg-orange text-white px-8 md:px-10 py-3 rounded-lg hover:bg-orange-dark transition duration-300 font-medium text-base md:text-lg shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  Get in Touch
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="border-2 border-orange text-orange px-8 md:px-10 py-3 rounded-lg hover:bg-orange hover:text-white transition duration-300 font-medium text-base md:text-lg w-full sm:w-auto"
                >
                  Learn More About Us
                </button>
              </div>
            </div>
          </motion.div>

          {/* Back to Top Button */}
          {showBackToTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-6 right-6 md:bottom-8 md:right-8 bg-orange text-white p-3 rounded-full shadow-lg hover:bg-orange-dark transition duration-300 z-50 hover:shadow-xl"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 md:h-6 md:w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </>
  );
}